import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import {
  USER_CAN_DEPOSIT,
  DELEGATE_MY_TASKS,
  TIP_CUE_WORKFLOW,
} from 'src/app/services/constants';

@Component({
  selector: 'app-permission-selector',
  templateUrl: './permission-selector.component.html',
  styleUrls: ['./permission-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PermissionSelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PermissionSelectorComponent),
      multi: true,
    },
  ],
})
export class PermissionSelectorComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() permissions: any[] = [];
  @Input() accessPermissions: any[] = [];
  @Input() userFeatures: {
    allocated?: boolean;
    pointsEligible?: boolean;
    sub_budget_allocation?: boolean;
  } = {};

  permissionForm!: FormGroup;
  isLevelNineSelected: boolean = false;
  isLevelTenSelected: boolean = false;
  blockedPermissionLabel: string = '';
  userCanDeposit = USER_CAN_DEPOSIT;
  private readonly allocatedBlockedLevels = [6, TIP_CUE_WORKFLOW, 11, 12];
  private readonly blockingPermissionLabels: Record<number, string> = {
    11: 'Outcome By Platform',
    12: 'TLE Based Tasking',
  };

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidationChange: () => void = () => {};

  constructor(private fb: FormBuilder) {}

  get visiblePermissions(): any[] {
    if (!this.userFeatures.allocated) {
      return this.permissions;
    }

    return this.permissions.filter(
      (permission) => !this.allocatedBlockedLevels.includes(permission.level)
    );
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupValueChanges();
  }

  private initializeForm(): void {
    this.permissionForm = this.fb.group({
      userRoles: [[]],
      userControls: [[]],
    });
  }

  private setupValueChanges(): void {
    this.permissionForm.valueChanges.subscribe(() => {
      this.emitValue();
      this.onValidationChange();
    });
  }

  setPermissions(selectedOptions: any[]) {
    const secondLevel = 2;
    const fifthLevel = 5;
    const sixthLevel = 6;
    const ninthLevel = 9;
    const tenthLevel = 10;
    const eleventhLevel = 11;
    const twelfthLevel = 12;

    const sixth = this.permissions.find((p) => p.level === sixthLevel);
    const ninth = this.permissions.find((p) => p.level === ninthLevel);

    if (!sixth || !ninth) return;

    const hasSixth = selectedOptions.some((opt) => opt.level === sixthLevel);
    const hasNinth = selectedOptions.some((opt) => opt.level === ninthLevel);
    const hasTenth = selectedOptions.some((opt) => opt.level === tenthLevel);
    const hasEleventh = selectedOptions.some((opt) => opt.level === eleventhLevel);
    const hasTwelfth = selectedOptions.some((opt) => opt.level === twelfthLevel);

    let updatedSelection = this.sanitizeRoleSelection(selectedOptions);

    // Rule 1: If level 6 is selected, auto-select all levels 1 to 5
    if (hasSixth) {
      for (let lvl = 1; lvl <= fifthLevel; lvl++) {
        const levelPerm = this.permissions.find((p) => p.level === lvl);
        if (levelPerm && !updatedSelection.some((opt) => opt.level === lvl)) {
          updatedSelection.push(levelPerm);
        }
      }
    }

    // Rule 2: If level 9 is selected, auto-select levels 1-5 only (6 and 10 can be checked manually)
    if (hasNinth) {
      for (let lvl = 1; lvl <= fifthLevel; lvl++) {
        const levelPerm = this.permissions.find((p) => p.level === lvl);
        if (levelPerm && !updatedSelection.some((opt) => opt.level === lvl)) {
          updatedSelection.push(levelPerm);
        }
      }
    }

    // Rule 3: If level 10 is selected, auto-select all levels 1 to 5
    if (hasTenth) {
      for (let lvl = 1; lvl <= fifthLevel; lvl++) {
        const levelPerm = this.permissions.find((p) => p.level === lvl);
        if (levelPerm && !updatedSelection.some((opt) => opt.level === lvl)) {
          updatedSelection.push(levelPerm);
        }
      }
    }

    // Rule 4: If level 11 is selected, auto-select all levels 1 to 5
    if (hasEleventh) {
      for (let lvl = 1; lvl <= secondLevel; lvl++) {
        const levelPerm = this.permissions.find((p) => p.level === lvl);
        if (levelPerm && !updatedSelection.some((opt) => opt.level === lvl)) {
          updatedSelection.push(levelPerm);
        }
      }
    }

    // Rule 5: If level 12 is selected, auto-select all levels 1 to 5
    if (hasTwelfth) {
      for (let lvl = 1; lvl <= secondLevel; lvl++) {
        const levelPerm = this.permissions.find((p) => p.level === lvl);
        if (levelPerm && !updatedSelection.some((opt) => opt.level === lvl)) {
          updatedSelection.push(levelPerm);
        }
      }
    }

    this.isLevelNineSelected = hasNinth;
    this.isLevelTenSelected = updatedSelection.some((opt) => opt.level === 10);
    this.updateBlockedPermissionLabel(updatedSelection);
    this.permissionForm.get('userRoles')?.setValue(updatedSelection, { emitEvent: false });
    this.emitValue();
  }

  private updateBlockedPermissionLabel(selectedOptions: any[]): void {
    const blockingPermission = [12, 11, 10, TIP_CUE_WORKFLOW, 6]
      .map((level) => selectedOptions.find((option) => option.level === level))
      .find(Boolean);

    this.blockedPermissionLabel = blockingPermission
      ? this.blockingPermissionLabels[blockingPermission.level] || blockingPermission.permission || ''
      : '';
  }

  close(mySelect: MatSelect, event: Event): void {
    event.stopPropagation();
    mySelect.close();
  }

  isFifthBlocked(option: any): boolean {
    const selected = this.permissionForm.get('userRoles')?.value || [];
    const hasSixth = selected.some((opt: any) => opt.level === 6);
    const hasNinth = selected.some((opt: any) => opt.level === 9);
    const hasTenth = selected.some((opt: any) => opt.level === 10);
    const hasEleventh = selected.some((opt: any) => opt.level === 11);
    const hasTwelfth = selected.some((opt: any) => opt.level === 12);

    if (hasEleventh || hasTwelfth) {
      return option.level >= 1 && option.level <= 2;
    }

    return option.level >= 1 && option.level <= 5 && (hasSixth || hasNinth || hasTenth);
  }

  getPermissionTooltip(option: any): string {
    if (!this.isFifthBlocked(option)) return '';
    if (this.blockedPermissionLabel) {
      return `Unselect ${this.blockedPermissionLabel} first`;
    }
    return 'Unselect the Collection Point Tasking permission first';
  }

  get f() {
    return this.permissionForm.controls;
  }

  private emitValue(): void {
    const userRoles = this.permissionForm.get('userRoles')?.value || [];
    const userControls = this.permissionForm.get('userControls')?.value || [];
    
    // Combine both arrays and emit
    const combinedPermissions = [...userRoles, ...userControls];
    
    this.onChange(combinedPermissions);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value && Array.isArray(value)) {
      // Split incoming permissions into userRoles and userControls
      const accessLevels = [DELEGATE_MY_TASKS, USER_CAN_DEPOSIT];
      const userRoles = this.sanitizeRoleSelection(
        value.filter((p) => !accessLevels.includes(p.level))
      );
      const userControls = value.filter((p) => accessLevels.includes(p.level));

      this.permissionForm.patchValue(
        {
          userRoles: userRoles,
          userControls: userControls,
        },
        { emitEvent: false }
      );
      
      // Check if level 9 or 10 is selected
      this.isLevelNineSelected = userRoles.some((p) => p.level === TIP_CUE_WORKFLOW);
      this.isLevelTenSelected = userRoles.some((p) => p.level === 10);
      this.updateBlockedPermissionLabel(userRoles);
    }
  }

  private sanitizeRoleSelection(selectedOptions: any[]): any[] {
    if (!this.userFeatures.allocated) {
      return [...selectedOptions];
    }

    return selectedOptions.filter(
      (option) => !this.allocatedBlockedLevels.includes(option.level)
    );
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.permissionForm.disable();
    } else {
      this.permissionForm.enable();
    }
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.permissionForm) {
      return null;
    }

    const userRolesControl = this.permissionForm.get('userRoles');
    const errors: ValidationErrors = {};

    // Check if userRoles is required and empty
    if (userRolesControl?.hasError('required')) {
      errors['required'] = true;
    }

    // Check if userRoles has value but is empty array
    const userRolesValue = userRolesControl?.value || [];
    if (Array.isArray(userRolesValue) && userRolesValue.length === 0) {
      errors['required'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }
}
